import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { LanguageService } from '../../../core/i18n/language.service';
import { Icon } from '../icon/icon';
import { BaseControl } from './base-control';

/**
 * Numeric field matching the Papercast `Input` design: a 48px sunken shell
 * (surface-1 fill, hairline ring, 8px radius), label below the field, plus an
 * optional trailing unit (e.g. "min", "%"). With `[stepper]="true"` it becomes
 * the DS `AmountStepper`: +/- icon buttons (32×24, chromeless) sit next to the
 * shell and nudge the value by `step`. ControlValueAccessor over a
 * `number | null` — blanks coerce to null.
 *
 *   <app-number-input [(ngModel)]="latitude" label="Latitude" [min]="-90" [max]="90" />
 *   <app-number-input [(ngModel)]="count" [stepper]="true" [min]="0" />
 */
@Component({
  selector: 'app-number-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumberInput), multi: true }],
  template: `
    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <div
          class="shell flex min-w-0 flex-1 items-center gap-2 rounded-control px-2 focus-within:ring-2 focus-within:ring-focus"
          [class.min-h-12]="size() === 'default'"
          [class.min-h-9]="size() === 'sm'"
          [class.bg-sunken]="!invalid()"
          [class.shadow-hairline]="!invalid()"
          [class.bg-danger-surface]="invalid()"
          [class.ring-1]="invalid()"
          [class.ring-danger]="invalid()"
          [class.opacity-70]="disabled()"
        >
          <input
            type="number"
            [value]="value() ?? ''"
            [placeholder]="effectivePlaceholder()"
            [disabled]="disabled()"
            [min]="min()"
            [max]="max()"
            [step]="step()"
            [attr.aria-label]="label() || null"
            [attr.aria-invalid]="invalid() || null"
            (input)="onInput($any($event.target).value)"
            (blur)="onTouched()"
            class="native min-w-0 flex-1 border-none bg-transparent p-0 text-ink-1 outline-none placeholder:text-ink-3"
            [class.text-[16px]]="size() === 'default'"
            [class.leading-6]="size() === 'default'"
            [class.text-[14px]]="size() === 'sm'"
            [class.leading-5]="size() === 'sm'"
          />
          @if (unit()) {
            <span class="flex-none text-[14px] font-semibold text-ink-3">{{ unit() }}</span>
          }
        </div>

        @if (stepper()) {
          <button
            type="button"
            [disabled]="disabled()"
            (click)="nudge(-1)"
            class="flex h-8 w-8 flex-none items-center justify-center rounded-control text-action enabled:hover:bg-sunken disabled:opacity-40"
            [attr.aria-label]="decreaseLabel()"
          >
            <app-icon name="icon-minus" [size]="18" />
          </button>
          <button
            type="button"
            [disabled]="disabled()"
            (click)="nudge(1)"
            class="flex h-8 w-8 flex-none items-center justify-center rounded-control text-action enabled:hover:bg-sunken disabled:opacity-40"
            [attr.aria-label]="increaseLabel()"
          >
            <app-icon name="icon-content-add24px" [size]="18" />
          </button>
        }
      </div>

      @if (label()) {
        <span class="px-2 text-caption font-semibold text-ink-2">{{ label() }}</span>
      }
    </div>
  `,
  styles: `
    /* The DS field is clean — kill the native number spinners (the external
       -/+ steppers or keyboard cover increment). */
    .native::-webkit-outer-spin-button,
    .native::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .native {
      -moz-appearance: textfield;
      appearance: textfield;
    }

    /* Hover strengthens the hairline ring to the strong overlay (DS hover
       state); suppressed while focused, where the 2px blue focus ring rules. */
    .shell:hover:not(:focus-within) {
      box-shadow: inset 0 0 0 1px var(--border-strong);
    }
  `,
})
export class NumberInput extends BaseControl<number> {
  private readonly transloco = inject(TranslocoService);
  private readonly language = inject(LanguageService);

  readonly label = input('');
  readonly placeholder = input('');
  readonly unit = input('');
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);
  readonly step = input<number | string | null>(null);
  readonly invalid = input(false);
  /** Shell height: 48px default, 36px small (settings rows, toolbars). */
  readonly size = input<'default' | 'sm'>('default');
  /** Render trailing +/- buttons wired to `step` (DS `AmountStepper`). */
  readonly stepper = input(false);

  protected readonly hasValue = computed(() => this.value() != null);

  /** The label doubles as the placeholder when no explicit placeholder is set. */
  protected readonly effectivePlaceholder = computed(() => this.placeholder() || this.label());

  protected readonly increaseLabel = computed(() => {
    this.language.current();
    return this.transloco.translate('forms.stepper.increase');
  });

  protected readonly decreaseLabel = computed(() => {
    this.language.current();
    return this.transloco.translate('forms.stepper.decrease');
  });

  protected onInput(raw: string): void {
    this.setValue(raw === '' ? null : Number(raw));
  }

  /** Step the value up/down, clamped to min/max; a blank field starts from min (or 0). */
  protected nudge(direction: 1 | -1): void {
    const step = Number(this.step()) || 1;
    const base = this.value() ?? this.min() ?? 0;
    let next = this.value() == null ? base : base + direction * step;
    const min = this.min();
    const max = this.max();
    if (min != null) next = Math.max(min, next);
    if (max != null) next = Math.min(max, next);
    this.setValue(next);
    this.onTouched();
  }
}
